/**
 * PAID_ADS_DESIGN §3.4 Step 4 — the broadcast door dialog (Paid / Social / Both).
 *
 * Owner rule (§1 door 3): broadcasting a listing with no campaign or strategy
 * attached asks the agent Paid | Social | Both. This is the ONE shared dialog
 * for the listing-broadcast UX — mount it, never fork it.
 *
 * Honesty invariants:
 *  - Access denied (any cascade layer) shows the machine code + message from
 *    the API plus the upsell / admin-contact copy — never a stub success.
 *  - Provider not approved → the staged-pending copy; nothing pretends to
 *    deliver (the PaidChannelsPanel pattern).
 *  - Caps violations come back as inline field errors (INVALID_BUDGET names
 *    the cap).
 *  - "Let AI propose" does not fake an AI: the chooser that auto-fills the
 *    proposal ships in step 5 — the fields are agent-editable and the confirm
 *    is audited either way (the helper copy says so out loud).
 *  - Social mode routes to the existing social publishing flow — it is not
 *    rebuilt here.
 *
 * All creations are drafts — launch wiring is step 6 after strategy F6.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, Radio } from 'lucide-react'
import { api } from '@/api/client'
import type { PaidMarket, SpendSplit } from '@/api/paidAdsCampaigns'
import { useToast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useLocale } from '@/hooks/useLocale'
import { DoorDeniedState, DoorFieldError, DoorTargetingFields } from '../door-shared'
import { bcp, formatMicros } from './copy'

type Mode = 'paid' | 'social' | 'both'
type SplitKind = 'predefined' | 'ai' | 'manual'

export type BroadcastDoorDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The listing being broadcast (property id + display title). */
  listing: { id: string; title?: string } | null
  /**
   * Social mode routes to the existing social publishing path — the parent
   * opens its publish/distribute flow for this listing. Not rebuilt here.
   */
  onSocialContinue?: (listing: { id: string; title?: string }) => void
  onStaged?: (result: { honest_state: string; message: string; executionCount: number }) => void
}

const LAUNCH_PLATFORM = 'meta_ads'

export function BroadcastDoorDialog({
  open,
  onOpenChange,
  listing,
  onSocialContinue,
  onStaged,
}: BroadcastDoorDialogProps) {
  const { locale } = useLocale()
  const { addToast } = useToast()

  const [mode, setMode] = useState<Mode | null>(null)
  const [objective, setObjective] = useState('')
  const [budget, setBudget] = useState('')
  const [country, setCountry] = useState('')
  const [areas, setAreas] = useState('')
  const [radius, setRadius] = useState('')
  const [splitKind, setSplitKind] = useState<SplitKind>('predefined')
  const [splits, setSplits] = useState<SpendSplit[]>([])
  const [market, setMarket] = useState<PaidMarket | null>(null)
  const [selectedSplitId, setSelectedSplitId] = useState('')
  const [manualPaid, setManualPaid] = useState('60')
  const [manualSocial, setManualSocial] = useState('40')
  const [aiPaid, setAiPaid] = useState('50')
  const [aiSocial, setAiSocial] = useState('50')
  const [aiProposing, setAiProposing] = useState(false)
  const [aiNotice, setAiNotice] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [denied, setDenied] = useState<{ message: string; code?: string } | null>(null)
  const [loadingSplits, setLoadingSplits] = useState(false)
  const [staging, setStaging] = useState(false)
  const [staged, setStaged] = useState<{ pending: boolean; message: string } | null>(null)

  const reset = useCallback(() => {
    setMode(null)
    setObjective('')
    setBudget('')
    setCountry('')
    setAreas('')
    setRadius('')
    setSplitKind('predefined')
    setSelectedSplitId('')
    setManualPaid('60')
    setManualSocial('40')
    setAiPaid('50')
    setAiSocial('50')
    setAiProposing(false)
    setAiNotice(null)
    setFieldErrors({})
    setDenied(null)
    setStaged(null)
    setStaging(false)
  }, [])

  // Load the market's active spend splits + caps when the door opens. A 402/403
  // here is the access gate talking — show the denial instead of the form.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoadingSplits(true)
    setDenied(null)
    api
      .getPaidAdsSpendSplits(LAUNCH_PLATFORM)
      .then((res) => {
        if (cancelled) return
        setSplits(res.items || [])
        setMarket(res.market || null)
        setCountry(res.market?.country || '')
        const first = (res.items || [])[0]
        if (first) setSelectedSplitId(first.id)
      })
      .catch((err: any) => {
        if (cancelled) return
        if (err?.status === 402 || err?.status === 403) {
          setDenied({ message: err?.error || err?.message, code: err?.code })
        } else {
          addToast({ title: bcp('door.error.generic', locale), description: err?.message, variant: 'error' })
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingSplits(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, addToast, locale])

  const capsHelper = useMemo(() => {
    if (!market) return null
    const min = market.min_budget_micros
    const max = market.max_budget_micros
    if (min != null && max != null)
      return bcp('paid.budget.capsHelper', locale, {
        country: market.country,
        min: formatMicros(min),
        max: formatMicros(max),
      })
    if (min != null) return bcp('paid.budget.capsMinOnly', locale, { country: market.country, min: formatMicros(min) })
    if (max != null) return bcp('paid.budget.capsMaxOnly', locale, { country: market.country, max: formatMicros(max) })
    return null
  }, [market, locale])

  const chosenSplit: SpendSplit | null = useMemo(
    () => splits.find((split) => split.id === selectedSplitId) || null,
    [splits, selectedSplitId],
  )

  const paidPercent =
    splitKind === 'predefined'
      ? (chosenSplit?.paid_percent ?? null)
      : splitKind === 'ai'
        ? Number(aiPaid) || 0
        : Number(manualPaid) || 0
  const socialPercent =
    splitKind === 'predefined'
      ? (chosenSplit?.social_percent ?? null)
      : splitKind === 'ai'
        ? Number(aiSocial) || 0
        : Number(manualSocial) || 0

  /**
   * The real chooser (Step 5): the AI PROPOSES based on the listing + market;
   * the agent reviews/edits and confirms — nothing is created until the
   * confirm submit (broadcast kind=ai, audited). Provider down → honest notice.
   */
  async function proposeWithAi() {
    if (!listing) return
    setAiProposing(true)
    setAiNotice(null)
    try {
      const result = await api.createPaidAdsAiProposal({
        door: 'broadcast',
        mode: 'both',
        listing_id: listing.id,
        objective_draft: objective.trim() || undefined,
      })
      const proposal = result?.proposal
      if (!proposal) throw new Error('empty proposal')
      if (proposal.objective) setObjective(proposal.objective)
      if (proposal.budget_micros != null) setBudget(String(proposal.budget_micros / 1_000_000))
      if (proposal.paid_percent != null) setAiPaid(String(proposal.paid_percent))
      if (proposal.social_percent != null) setAiSocial(String(proposal.social_percent))
      const territory = proposal.targeting?.territory
      if (territory?.area_ids?.length) setAreas(territory.area_ids.join(', '))
      if (territory?.radius_meters) setRadius(String(territory.radius_meters))
    } catch (err: any) {
      setAiNotice(bcp('both.split.ai.unavailable', locale, { code: err?.code || bcp('door.error.generic', locale) }))
    } finally {
      setAiProposing(false)
    }
  }

  function buildTargeting() {
    const territory: Record<string, unknown> = {}
    if (country) territory.country = country.toUpperCase()
    const areaIds = areas
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
    if (areaIds.length) territory.area_ids = areaIds
    const radiusValue = Number(radius)
    if (radius) territory.radius_meters = radiusValue
    return Object.keys(territory).length ? { territory } : {}
  }

  function buildPayload() {
    if (!listing || !mode) return null
    const payload: Record<string, unknown> = {
      listing_id: listing.id,
      mode,
      objective: objective.trim(),
    }
    if (mode === 'paid') {
      payload.paid = {
        budget_micros: Math.round(Number(budget) * 1_000_000),
        currency: 'USD',
        targeting: buildTargeting(),
      }
    }
    if (mode === 'both') {
      payload.budget_micros = Math.round(Number(budget) * 1_000_000)
      payload.currency = 'USD'
      payload.targeting = buildTargeting()
      if (splitKind === 'predefined') {
        payload.split = { kind: 'predefined', spend_split_id: selectedSplitId }
      } else if (splitKind === 'ai') {
        // The agent edits and confirms the proposal; the confirm is audited
        // (backend action paid_ads.broadcast.ai_confirmed).
        payload.split = {
          kind: 'ai',
          proposal: {
            paid_percent: Number(aiPaid) || 0,
            social_percent: Number(aiSocial) || 0,
            objective: objective.trim(),
            targeting: buildTargeting(),
          },
        }
      } else {
        payload.split = {
          kind: 'manual',
          paid_percent: Number(manualPaid) || 0,
          social_percent: Number(manualSocial) || 0,
        }
      }
    }
    return payload as Parameters<typeof api.createPaidAdsBroadcastPlan>[0]
  }

  function validate(): boolean {
    const errors: Record<string, string> = {}
    if (!objective.trim()) errors.objective = bcp('door.objective.label', locale)
    if (mode === 'paid' || mode === 'both') {
      if (!budget || Number.isNaN(Number(budget)) || Number(budget) <= 0) {
        errors.budget = bcp('paid.budget.label', locale)
      }
      // Caps are NOT pre-checked here: the paid part of a split depends on
      // server-side rounding, so the server is the final word and lands
      // INVALID_BUDGET inline (the caps helper text above the field already
      // states the range).
    }
    if (mode === 'both' && splitKind === 'manual') {
      if ((Number(manualPaid) || 0) + (Number(manualSocial) || 0) !== 100) {
        errors.split = bcp('both.split.manual.sumError', locale)
      }
    }
    if (mode === 'both' && splitKind === 'ai') {
      if ((Number(aiPaid) || 0) + (Number(aiSocial) || 0) !== 100) {
        errors.split = bcp('both.split.manual.sumError', locale)
      }
    }
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  async function submit() {
    if (mode === 'social') {
      // Route to the existing social publishing flow — do not rebuild it.
      onOpenChange(false)
      if (listing) onSocialContinue?.(listing)
      return
    }
    if (!validate()) return
    const payload = buildPayload()
    if (!payload) return
    setStaging(true)
    setFieldErrors({})
    try {
      const result = await api.createPaidAdsBroadcastPlan(payload)
      const pending = result.honest_state === 'staged_pending_approval'
      setStaged({ pending, message: result.message })
      onStaged?.({
        honest_state: result.honest_state,
        message: result.message,
        executionCount: result.executions?.length || 0,
      })
    } catch (err: any) {
      if (err?.status === 400 && err?.code) {
        // Caps violations (INVALID_BUDGET) and split errors land inline.
        const field =
          err.code === 'INVALID_BUDGET'
            ? 'budget'
            : err.code === 'INVALID_SPLIT' || err.code?.includes('SPLIT')
              ? 'split'
              : 'form'
        setFieldErrors((prev) => ({ ...prev, [field]: err?.error || err?.message }))
      } else if (err?.status === 402 || err?.status === 403) {
        setDenied({ message: err?.error || err?.message, code: err?.code })
      } else {
        addToast({ title: bcp('door.error.generic', locale), description: err?.message, variant: 'error' })
      }
    } finally {
      setStaging(false)
    }
  }

  const modeOption = (value: Mode) => (
    <label
      key={value}
      className={`flex cursor-pointer items-start gap-3 rounded-[var(--lc-radius-md)] border p-3 transition-colors ${
        mode === value
          ? 'border-[var(--lc-border-strong)] bg-[var(--lc-surface-sunken)]'
          : 'border-[var(--lc-border)] bg-[var(--lc-surface)]'
      }`}
      data-testid={`mode-${value}`}
    >
      <input
        type="radio"
        name="broadcast-mode"
        value={value}
        checked={mode === value}
        onChange={() => setMode(value)}
        className="mt-1"
      />
      <span>
        <span className="block font-medium text-[var(--lc-text-primary)]">
          {bcp(`door.mode.${value}.label`, locale)}
        </span>
        <span className="block text-sm text-[var(--lc-text-muted)]">{bcp(`door.mode.${value}.hint`, locale)}</span>
      </span>
    </label>
  )

  const fieldError = (field: string) =>
    fieldErrors[field] ? <DoorFieldError error={fieldErrors[field]} testId={`error-${field}`} /> : null

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset()
        onOpenChange(next)
      }}
    >
      <DialogContent className="max-w-xl" data-testid="broadcast-door-dialog">
        <DialogHeader>
          <DialogTitle>{bcp('door.title', locale)}</DialogTitle>
          <DialogDescription>
            {listing?.title ? `${listing.title} · ` : ''}
            {bcp('door.description', locale)}
          </DialogDescription>
        </DialogHeader>

        {loadingSplits ? (
          <div className="flex items-center gap-2 py-6 text-sm text-[var(--lc-text-muted)]">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> …
          </div>
        ) : denied ? (
          <DoorDeniedState
            title={bcp('door.denied.title', locale)}
            message={denied.message}
            code={denied.code}
            upsell={bcp('door.denied.upsell', locale)}
            testId="broadcast-denied"
          />
        ) : staged ? (
          <div
            role="status"
            className="space-y-2 rounded-[var(--lc-radius-md)] border border-[var(--lc-border)] bg-[var(--lc-surface-sunken)] p-4"
            data-testid="broadcast-staged"
          >
            <p className="flex items-center gap-2 font-medium text-[var(--lc-text-heading)]">
              <Radio className="h-4 w-4" aria-hidden />
              {staged.pending ? bcp('door.staged.pendingTitle', locale) : bcp('door.title', locale)}
            </p>
            <p className="text-sm text-[var(--lc-text-muted)]">
              {staged.pending ? bcp('door.staged.pendingBody', locale) : bcp('door.staged.readyBody', locale)}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="broadcast-objective">{bcp('door.objective.label', locale)}</Label>
              <Input
                id="broadcast-objective"
                value={objective}
                onChange={(event) => setObjective(event.target.value)}
                placeholder={bcp('door.objective.placeholder', locale)}
                className="bg-[var(--lc-surface-sunken)]"
              />
              {fieldError('objective')}
            </div>

            <div className="space-y-2" role="radiogroup" aria-label={bcp('door.title', locale)}>
              {modeOption('paid')}
              {modeOption('social')}
              {modeOption('both')}
            </div>

            {mode === 'paid' || mode === 'both' ? (
              <div className="space-y-3 rounded-[var(--lc-radius-md)] border border-[var(--lc-border)] bg-[var(--lc-surface)] p-3">
                <div className="space-y-2">
                  <Label htmlFor="broadcast-budget">
                    {mode === 'both' ? bcp('both.total.label', locale) : bcp('paid.budget.label', locale)}
                  </Label>
                  <Input
                    id="broadcast-budget"
                    inputMode="decimal"
                    value={budget}
                    onChange={(event) => setBudget(event.target.value)}
                    className="bg-[var(--lc-surface-sunken)]"
                  />
                  {capsHelper ? <p className="text-xs text-[var(--lc-text-muted)]">{capsHelper}</p> : null}
                  {fieldError('budget')}
                </div>

                <DoorTargetingFields
                  country={country}
                  onCountryChange={setCountry}
                  areas={areas}
                  onAreasChange={setAreas}
                  radius={radius}
                  onRadiusChange={setRadius}
                  labels={{
                    country: bcp('paid.targeting.country.label', locale),
                    countryHelper: market?.country
                      ? bcp('paid.targeting.country.helper', locale, { country: market.country })
                      : undefined,
                    areas: bcp('paid.targeting.areas.label', locale),
                    areasPlaceholder: bcp('paid.targeting.areas.placeholder', locale),
                    radius: bcp('paid.targeting.radius.label', locale),
                  }}
                />

                {mode === 'both' ? (
                  <fieldset className="space-y-2" data-testid="split-section">
                    <legend className="text-sm font-medium text-[var(--lc-text-primary)]">
                      {bcp('both.split.title', locale)}
                    </legend>

                    <label className="flex items-start gap-2" data-testid="split-predefined">
                      <input
                        type="radio"
                        name="broadcast-split"
                        checked={splitKind === 'predefined'}
                        onChange={() => setSplitKind('predefined')}
                      />
                      <span className="text-sm text-[var(--lc-text-primary)]">
                        {bcp('both.split.predefined.label', locale)}
                      </span>
                    </label>
                    {splitKind === 'predefined' ? (
                      splits.length ? (
                        <div className="ms-6 space-y-1">
                          {splits.map((split) => (
                            <label
                              key={split.id}
                              className="flex items-center gap-2 text-sm text-[var(--lc-text-secondary)]"
                            >
                              <input
                                type="radio"
                                name="broadcast-split-choice"
                                checked={selectedSplitId === split.id}
                                onChange={() => setSelectedSplitId(split.id)}
                              />
                              {split.name} — {split.paid_percent}% / {split.social_percent}%
                            </label>
                          ))}
                        </div>
                      ) : (
                        <p className="ms-6 text-sm text-[var(--lc-text-muted)]">
                          {bcp('both.split.predefined.empty', locale)}
                        </p>
                      )
                    ) : null}

                    <label className="flex items-start gap-2" data-testid="split-ai">
                      <input
                        type="radio"
                        name="broadcast-split"
                        checked={splitKind === 'ai'}
                        onChange={() => setSplitKind('ai')}
                      />
                      <span className="text-sm text-[var(--lc-text-primary)]">
                        {bcp('both.split.ai.label', locale)}
                      </span>
                    </label>
                    {splitKind === 'ai' ? (
                      <div className="ms-6 space-y-2">
                        <p className="text-xs text-[var(--lc-text-muted)]">{bcp('both.split.ai.helper', locale)}</p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={proposeWithAi}
                          disabled={aiProposing}
                          data-testid="ai-propose"
                        >
                          {aiProposing ? (
                            <Loader2 className="me-2 h-4 w-4 animate-spin" aria-hidden />
                          ) : null}
                          {aiProposing ? bcp('both.split.ai.proposing', locale) : bcp('both.split.ai.propose', locale)}
                        </Button>
                        {aiNotice ? (
                          <p role="alert" className="text-sm text-[var(--lc-status-underOffer-fg)]" data-testid="ai-notice">
                            {aiNotice}
                          </p>
                        ) : null}
                        <p className="text-sm font-medium text-[var(--lc-text-primary)]">
                          {bcp('both.split.ai.proposedTitle', locale)}
                        </p>
                        <div className="flex gap-2">
                          <Input
                            aria-label={bcp('both.split.manual.paid', locale)}
                            inputMode="numeric"
                            value={aiPaid}
                            onChange={(event) => setAiPaid(event.target.value)}
                            className="bg-[var(--lc-surface-sunken)]"
                          />
                          <Input
                            aria-label={bcp('both.split.manual.social', locale)}
                            inputMode="numeric"
                            value={aiSocial}
                            onChange={(event) => setAiSocial(event.target.value)}
                            className="bg-[var(--lc-surface-sunken)]"
                          />
                        </div>
                      </div>
                    ) : null}

                    <label className="flex items-start gap-2" data-testid="split-manual">
                      <input
                        type="radio"
                        name="broadcast-split"
                        checked={splitKind === 'manual'}
                        onChange={() => setSplitKind('manual')}
                      />
                      <span className="text-sm text-[var(--lc-text-primary)]">
                        {bcp('both.split.manual.label', locale)}
                      </span>
                    </label>
                    {splitKind === 'manual' ? (
                      <div className="ms-6 flex gap-2">
                        <Input
                          aria-label={bcp('both.split.manual.paid', locale)}
                          inputMode="numeric"
                          value={manualPaid}
                          onChange={(event) => setManualPaid(event.target.value)}
                          className="bg-[var(--lc-surface-sunken)]"
                        />
                        <Input
                          aria-label={bcp('both.split.manual.social', locale)}
                          inputMode="numeric"
                          value={manualSocial}
                          onChange={(event) => setManualSocial(event.target.value)}
                          className="bg-[var(--lc-surface-sunken)]"
                        />
                      </div>
                    ) : null}
                    {paidPercent != null && socialPercent != null ? (
                      <p className="text-xs text-[var(--lc-text-muted)]" data-testid="split-summary">
                        {paidPercent}% / {socialPercent}%
                      </p>
                    ) : null}
                    {fieldError('split')}
                  </fieldset>
                ) : null}
              </div>
            ) : null}
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              reset()
              onOpenChange(false)
            }}
          >
            {bcp('door.cancel', locale)}
          </Button>
          {mode && !denied && !staged ? (
            <Button type="button" onClick={submit} disabled={staging}>
              {staging ? <Loader2 className="me-2 h-4 w-4 animate-spin" aria-hidden /> : null}
              {staging ? bcp('door.submit.staging', locale) : bcp(`door.submit.${mode}`, locale)}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
